// src/components/CallSection.js - גרסה מעודכנת עם טיפול משופר בתגובות שיחה
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  Text,
  VStack,
  HStack,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Flex,
  Icon,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
  useColorModeValue,
  Tooltip,
  NumberInput,
  NumberInputField,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInputStepper,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel
} from '@chakra-ui/react';
import { 
  PhoneIcon, 
  SmallCloseIcon, 
  CheckIcon, 
  WarningIcon, 
  NotAllowedIcon
} from '@chakra-ui/icons';
import { 
  MdCall, 
  MdCallEnd, 
  MdCallMade, 
  MdCallReceived,
  MdPhone,
  MdAttachMoney,
  MdMoneyOff,
  MdLoop,
  MdCallSplit
} from 'react-icons/md';
import { db, auth } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc,
  addDoc, 
  updateDoc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { io } from 'socket.io-client';

// פונקציות עזר
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let cleanPhone = phone.replace(/[^\d+]/g, '');
  if (cleanPhone.startsWith('+972')) {
    return cleanPhone;
  }
  if (cleanPhone.startsWith('972')) {
    return '+' + cleanPhone;
  }
  if (cleanPhone.startsWith('0')) {
    return '+972' + cleanPhone.slice(1);
  }
  return '+972' + cleanPhone;
};

const displayPhoneNumber = (phone) => {
  if (!phone) return '';
  if (phone.startsWith('+972')) {
    return '0' + phone.slice(4);
  }
  return phone;
};

const CallSection = ({ selectedContacts, serverUrl, serverStatus }) => {
  // State variables
  const [agentPhone, setAgentPhone] = useState('');
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [callStatus, setCallStatus] = useState('idle');
  const [callsMade, setCallsMade] = useState({});
  const [isCallActive, setIsCallActive] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [socket, setSocket] = useState(null);
  const [realTimeUpdates, setRealTimeUpdates] = useState({});
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [userBalance, setUserBalance] = useState(0);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [isDiggingMode, setIsDiggingMode] = useState(false);
  const [isTerrorMode, setIsTerrorMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [callLogIds, setCallLogIds] = useState({});
  const [userDocument, setUserDocument] = useState(null);
  const [activeCallTimers, setActiveCallTimers] = useState({}); // טיימרים לחיוב בזמן אמת

  const toast = useToast();

  // צבעים עבור Dark/Light mode
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // יצירת או קבלת משתמש ב-Firestore
  const ensureUserExists = async () => {
    const user = auth.currentUser;
    if (!user) return null;

    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // console.log('Creating new user document...');
        const userData = {
          email: user.email || '',
          name: user.displayName || '',
          phone: user.phoneNumber || '',
          role: 'user',
          credits: 0,
          total_spent: 0,
          features: {
            digging_mode: true,
            unlimited_calls: false,
            priority_support: false
          },
          stats: {
            total_calls: 0,
            successful_calls: 0,
            failed_calls: 0
          },
          settings: {
            max_attempts: 3,
            call_timeout: 30
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        await setDoc(userRef, userData);
        // console.log('User document created successfully');
        setUserDocument(userData);
        return userData;
      } else {
        const userData = userDoc.data();
        setUserDocument(userData);
        return userData;
      }
    } catch (error) {
      console.error('Error ensuring user exists:', error);
      return null;
    }
  };

  // שמירת call log ל-Firebase
  const saveCallToFirebase = async (callData, contactInfo = null) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('No authenticated user to save call');
        return null;
      }

      const sessionId = callData.sessionId;
      
      if (callLogIds[sessionId]) {
        // עדכן call log קיים
        const updateData = {
          status: callData.status,
          success: ['completed', 'charged', 'agent_answered', 'digging_completed'].includes(callData.status),
          ended_at: callData.timestamp || new Date().toISOString(),
          end_reason: callData.details || '',
          attempts: callData.attempt || 1,
          duration: callData.duration || 0,
          cost_usd: callData.cost_info?.cost_usd || 0,
          cost_ils: callData.cost_info?.cost_ils || 0,
          updated_at: new Date().toISOString()
        };

        try {
          const docRef = doc(db, 'call_logs', callLogIds[sessionId]);
          await updateDoc(docRef, updateData);
          // console.log('Call log updated in Firebase:', sessionId);
        } catch (updateError) {
          // console.warn('Update failed, creating new document:', updateError);
          const newCallLog = {
            session_id: sessionId,
            user_id: user.uid,
            ...updateData
          };
          const newDocRef = await addDoc(collection(db, 'call_logs'), newCallLog);
          setCallLogIds(prev => ({ ...prev, [sessionId]: newDocRef.id }));
        }
        return callLogIds[sessionId];
      } else {
        // צור call log חדש
        const callLog = {
          session_id: sessionId,
          user_id: user.uid,
          to_number: callData.to_number || contactInfo?.phone || '',
          agent_number: callData.agent_number || null,
          mode: callData.mode || 'normal',
          status: callData.status,
          success: ['completed', 'charged', 'agent_answered', 'digging_completed'].includes(callData.status),
          started_at: callData.started_at || new Date().toISOString(),
          ended_at: callData.timestamp || null,
          attempts: callData.attempt || 1,
          duration: callData.duration || 0,
          cost_usd: callData.cost_info?.cost_usd || 0,
          cost_ils: callData.cost_info?.cost_ils || 0,
          end_reason: callData.details || '',
          created_at: new Date().toISOString()
        };

        try {
          const docRef = await addDoc(collection(db, 'call_logs'), callLog);
          setCallLogIds(prev => ({ ...prev, [sessionId]: docRef.id }));
          // console.log('New call log saved to Firebase:', sessionId);
          return docRef.id;
        } catch (createError) {
          console.error('Error creating call log:', createError);
          return null;
        }
      }
    } catch (error) {
      console.error('Error saving call to Firebase:', error);
      return null;
    }
  };

  // טיפול בחיוב משתמש
  const handleUserCharge = async (callData) => {
    try {
      console.log('BILLING: handleUserCharge called with:', callData);
      const user = auth.currentUser;
      if (!user || !callData.cost_info?.cost_usd || callData.cost_info.cost_usd <= 0) {
        console.log('BILLING: No charge - missing user or cost info');
        return;
      }

      // קרא את היתרה העדכנית ביותר מ-Firebase
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentBalance = userDoc.exists() ? (userDoc.data().credits || 0) : 0;
      
      const newBalance = Math.max(0, currentBalance - callData.cost_info.cost_usd);
      setUserBalance(newBalance);
      
      console.log('BILLING: Updating balance:', {
        currentBalance,
        costUsd: callData.cost_info.cost_usd,
        newBalance
      });

      try {
        await addDoc(collection(db, 'billing_logs'), {
          user_id: user.uid,
          type: 'call_charge',
          amount: callData.cost_info.cost_usd,
          description: `חיוב עבור שיחה למספר ${callData.to_number}`,
          previous_balance: currentBalance,
          new_balance: newBalance,
          metadata: {
            session_id: callData.sessionId,
            call_mode: callData.mode,
            duration_seconds: callData.duration || 0,
            rate_per_minute: callData.rate || '$0.29/min'
          },
          timestamp: new Date().toISOString()
        });

        // תמיד נעדכן את המשתמש ב-Firebase
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const currentUserData = userDoc.exists() ? userDoc.data() : { total_spent: 0 };
        
        await updateDoc(userRef, {
          credits: newBalance,
          total_spent: (currentUserData.total_spent || 0) + callData.cost_info.cost_usd,
          updated_at: new Date().toISOString()
        });
        
        console.log('BILLING: Updated user in Firebase');

        toast({
          title: '💰 חיוב בוצע',
          description: `חויב: $${callData.cost_info.cost_usd.toFixed(2)} | יתרה חדשה: $${newBalance.toFixed(2)}`,
          status: 'info',
          duration: 5000,
          isClosable: true
        });
      } catch (error) {
        console.error('Error saving billing log:', error);
      }
    } catch (error) {
      console.error('Error charging user:', error);
      toast({
        title: 'שגיאה בחיוב',
        description: 'נכשל בעדכון החיוב',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  // התחברות ל-Socket.IO
  useEffect(() => {
    if (!serverUrl) {
      setConnectionStatus('disconnected');
      return;
    }

    // console.log('Initializing Socket.IO connection...');
    
    const newSocket = io(serverUrl, {
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 3,
      timeout: 10000,
      forceNew: true,
      transports: ['polling'],
      upgrade: false
    });
    
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('BILLING DEBUG: Connected to server via Socket.IO (polling mode)');
      setConnectionStatus('connected');
      toast({
        title: 'חיבור הוקם ✅',
        description: 'מחובר לשרת דרך HTTP polling',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    });

    newSocket.on('disconnect', (reason) => {
      // console.log('Disconnected from server:', reason);
      setConnectionStatus('disconnected');
      if (reason !== 'io client disconnect') {
        toast({
          title: 'חיבור נותק',
          description: 'הקשר עם השרת נותק - מנסה להתחבר מחדש',
          status: 'warning',
          duration: 3000,
          isClosable: true
        });
      }
    });

    newSocket.on('connect_error', (error) => {
      console.warn('Socket connection error (expected):', error.message);
      setConnectionStatus('error');
    });

    // האזנה לעדכונים בזמן אמת
    newSocket.on('call_update', async (data) => {
      console.log('BILLING DEBUG: Real-time update received:', data);
      
      const { sessionId, status, details, cost_info, mode } = data;
      
      // עדכון הסטטוס המקומי
      setRealTimeUpdates(prev => ({
        ...prev,
        [sessionId]: {
          ...data,
          last_update: new Date().toISOString()
        }
      }));

      // עדכון callsMade
      setCallsMade(prev => {
        const updated = { ...prev };
        for (const [contactId, callInfo] of Object.entries(updated)) {
          if (callInfo.call_id === sessionId) {
            updated[contactId] = {
              ...callInfo,
              status,
              details,
              realtime_data: data,
              cost_info,
              mode,
              last_update: data.timestamp
            };
          }
        }
        return updated;
      });

      // שמירה ל-Firebase בעדכונים חשובים
      const shouldSave = [
        'initiated', 'answered', 'agent_answered', 'completed', 
        'failed', 'charged', 'no_charge', 'timeout', 'busy', 'rejected',
        'digging_started', 'digging_completed', 'digging_error'
      ].includes(status);

      if (shouldSave) {
        const contactInfo = Object.entries(callsMade).find(
          ([, callInfo]) => callInfo.call_id === sessionId
        );
        const contact = contactInfo ? contacts.find(c => c.id === contactInfo[0]) : null;
        await saveCallToFirebase(data, contact);
      }

      // טיפול בחיוב כשהשיחה מסתיימת בעלות
      if (status === 'charged' && cost_info?.cost_usd > 0) {
        console.log('BILLING: Received charge event:', {
          sessionId,
          status,
          cost_info,
          userBalance,
          userDocument
        });
        await handleUserCharge(data);
      }

      // עדכון callStatus הכללי עם הודעות ברורות יותר
      if (mode === 'terror') {
        if (status === 'terror_started') {
          setCallStatus(`🔥 מצב טרור הופעל! 5 שיחות במקביל`);
        } else if (status === 'terror_wave') {
          setCallStatus(`🔥 גל התקפה! ${data.wave_size} שיחות במקביל...`);
        } else if (status === 'terror_call') {
          setCallStatus(`🔥 שיחת טרור #${data.call_number} (${data.active_calls} פעילות)`);
        } else if (status === 'terror_answered') {
          setCallStatus(`🔥 שיחה נענתה! ממשיך להציף...`);
        } else if (status === 'terror_completed') {
          setCallStatus(`🔥 מצב טרור הושלם - ${data.total_calls} שיחות!`);
          setIsCallActive(false);
        }
      } else if (mode === 'digging') {
        if (status === 'digging_started') {
          setCallStatus(`מצב חפירה החל - שיחה כל ${data.interval_seconds || 5} שניות`);
        } else if (status === 'digging_call') {
          setCallStatus(`שיחת חפירה #${data.attempt} - ללא חיוב`);
        } else if (status === 'digging_answered') {
          setCallStatus(`שיחת חפירה נענתה - מנתק מיידי (ללא חיוב)`);
        } else if (status === 'digging_completed') {
          setCallStatus(`מצב חפירה הושלם - ${data.total_attempts} שיחות (ללא חיוב)`);
          setIsCallActive(false);
        }
      } else {
        if (status === 'attempting') {
          setCallStatus(`מנסה להתקשר - ניסיון ${data.attempt} (ללא חיוב)`);
        } else if (status === 'ringing') {
          setCallStatus('שיחה מצלצלת... (עדיין ללא חיוב)');
        } else if (status === 'answered') {
          console.log('BILLING DEBUG: Call answered - starting immediate billing');
          console.log('BILLING DEBUG: Current mode:', mode);
          console.log('BILLING DEBUG: Is digging mode?', mode === 'digging');
          
          setCallStatus('השיחה נענתה - החיוב התחיל! 💰');
          
          // חיוב רק למצב רגיל (לא למצב חפירה)
          if (mode !== 'digging') {
            // חיוב ראשוני של דקה אחת מיד כשהשיחה נענית
            const initialCharge = {
              sessionId,
              status: 'answered',
              cost_info: {
                cost_usd: 0.29, // מינימום חיוב של דקה אחת
                cost_ils: 0.29 * 3.7,
                duration_seconds: 0,
                duration_minutes: 1,
                billable_minutes: 1
              },
              mode: mode || 'normal',
              timestamp: new Date().toISOString()
            };
            console.log('BILLING: Initial charge on answer:', initialCharge);
            await handleUserCharge(initialCharge);
          } else {
            console.log('BILLING DEBUG: Skipping billing - digging mode');
          }
          
          // הפעל טיימר לחיוב כל דקה - רק למצב רגיל
          if (mode !== 'digging') {
            const callStartTime = new Date();
            let lastChargedMinutes = 1; // כבר חייבנו דקה אחת
            
            const timer = setInterval(async () => {
              const now = new Date();
              const durationSeconds = Math.floor((now - callStartTime) / 1000);
              const durationMinutes = Math.ceil(durationSeconds / 60);
              
              if (durationMinutes > lastChargedMinutes) { // חייב רק את הדקות החדשות
                const additionalMinutes = durationMinutes - lastChargedMinutes;
                const additionalCharge = {
                  sessionId,
                  status: 'ongoing',
                  cost_info: {
                    cost_usd: additionalMinutes * 0.29,
                    cost_ils: additionalMinutes * 0.29 * 3.7,
                    duration_seconds: durationSeconds,
                    duration_minutes: durationMinutes,
                    billable_minutes: additionalMinutes
                  },
                  mode: mode || 'normal',
                  timestamp: new Date().toISOString()
                };
                console.log('BILLING: Additional charge for minutes:', additionalMinutes, 'Total duration:', durationMinutes);
                await handleUserCharge(additionalCharge);
                lastChargedMinutes = durationMinutes;
              }
            }, 30000); // בדוק כל 30 שניות
            
            setActiveCallTimers(prev => ({ ...prev, [sessionId]: timer }));
            console.log('BILLING DEBUG: Timer set for session:', sessionId);
          }
        } else if (status === 'agent_answered') {
          setCallStatus(`שיחה פעילה עם סוכן`);
        } else if (status === 'charged') {
          setCallStatus(`שיחה הסתיימה - ${cost_info ? `חויב $${cost_info.cost_usd.toFixed(2)}` : 'מחשב עלות...'}`);
        } else if (status === 'no_charge') {
          setCallStatus('שיחה הסתיימה - ללא חיוב');
        } else if (status === 'timeout') {
          setCallStatus(`לא נענה (ניסיון ${data.attempt || 1}) - ללא חיוב`);
        } else if (status === 'busy') {
          setCallStatus(`קו תפוס (ניסיון ${data.attempt || 1}) - ללא חיוב`);
        } else if (status === 'rejected') {
          setCallStatus(`השיחה נדחתה (ניסיון ${data.attempt || 1}) - ללא חיוב`);
        } else if (status === 'error') {
          setCallStatus(`שגיאה טכנית בניסיון ${data.attempt || 1}`);
        } else if (status === 'retrying') {
          setCallStatus(`מנסה שוב - ניסיון ${data.attempt || 1} מתוך ${data.max_attempts || 3}`);
        } else if (status === 'ended') {
          // Don't update callStatus - wait for the next status
        } else if (status === 'completed') {
          setIsCallActive(false);
          // נקה את הטיימר של החיוב
          if (activeCallTimers[sessionId]) {
            clearInterval(activeCallTimers[sessionId]);
            setActiveCallTimers(prev => {
              const newTimers = { ...prev };
              delete newTimers[sessionId];
              return newTimers;
            });
          }
        }
      }

      // הודעות טוסט מעודכנות
      if (mode === 'terror') {
        if (status === 'terror_started') {
          toast({
            title: '🔥 מצב טרור הופעל!',
            description: `5 שיחות במקביל כל שנייה - ללא חיוב`,
            status: 'error',
            duration: 4000,
            isClosable: true
          });
        } else if (status === 'terror_answered') {
          toast({
            title: '🔥 שיחה נענתה!',
            description: 'ממשיך להציף - ללא חיוב',
            status: 'warning',
            duration: 2000,
            isClosable: true
          });
        } else if (status === 'terror_completed') {
          toast({
            title: '🔥 מצב טרור הושלם',
            description: `${data.total_calls} שיחות בוצעו - ללא חיוב`,
            status: 'info',
            duration: 5000,
            isClosable: true
          });
        }
      } else if (mode === 'digging') {
        if (status === 'digging_started') {
          toast({
            title: '🔄 מצב חפירה החל!',
            description: `התקשרות כל ${data.interval_seconds || 5} שניות - ללא חיוב`,
            status: 'info',
            duration: 4000,
            isClosable: true
          });
        } else if (status === 'digging_answered') {
          toast({
            title: '📞 שיחת חפירה נענתה!',
            description: 'מנתק מיידי - ללא חיוב',
            status: 'success',
            duration: 3000,
            isClosable: true
          });
        } else if (status === 'digging_completed') {
          toast({
            title: '✅ מצב חפירה הושלם',
            description: `${data.total_attempts} שיחות בוצעו - ללא חיוב`,
            status: 'info',
            duration: 5000,
            isClosable: true
          });
        }
      } else {
        if (status === 'answered') {
          toast({
            title: '🎉 שיחה נענתה!',
            description: `החיוב מתחיל כעת - $0.29/דקה`,
            status: 'success',
            duration: 4000,
            isClosable: true
          });
        } else if (status === 'timeout') {
          toast({
            title: '⏰ לא נענה',
            description: `ניסיון ${data.attempt || 1} - ללא חיוב`,
            status: 'warning',
            duration: 2000,
            isClosable: true
          });
        } else if (status === 'busy') {
          toast({
            title: '📞 קו תפוס',
            description: `ניסיון ${data.attempt || 1} - ללא חיוב`,
            status: 'warning',
            duration: 2000,
            isClosable: true
          });
        } else if (status === 'rejected') {
          toast({
            title: '🚫 השיחה נדחתה',
            description: `ניסיון ${data.attempt || 1} - ללא חיוב`,
            status: 'warning',
            duration: 2000,
            isClosable: true
          });
        } else if (status === 'retrying') {
          toast({
            title: '🔄 מנסה שוב',
            description: `עובר לניסיון ${data.attempt || 1} מתוך ${data.max_attempts || 3}`,
            status: 'info',
            duration: 1500,
            isClosable: true
          });
        }
      }
    });

    return () => {
      // console.log('Cleaning up Socket.IO connection');
      if (newSocket) {
        newSocket.removeAllListeners();
        newSocket.close();
      }
    };
  }, [serverUrl]);

  // אתחול משתמש ויתרה
  useEffect(() => {
    const initializeUser = async () => {
      if (auth.currentUser) {
        await ensureUserExists();
        await fetchUserBalance();
        await fetchPricingInfo();
      }
    };

    initializeUser();
  }, []);

  // שליפת יתרה
  const fetchUserBalance = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return 0;

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const balance = userData.credits || 0;
        setUserBalance(balance);
        setUserDocument(userData);
        console.log('BILLING DEBUG: Fetched balance from Firebase:', balance);
        return balance;
      } else {
        setUserBalance(0);
        return 0;
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      setUserBalance(0);
      return 0;
    }
  };

  // שליפת מידע תמחור
  const fetchPricingInfo = async () => {
    try {
      const response = await fetch(`${serverUrl}/api/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricingInfo(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  // שליפת אנשי קשר
  useEffect(() => {
    const fetchContacts = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const contactsRef = collection(db, `users/${user.uid}/contacts`);
          const snapshot = await getDocs(contactsRef);
          const contactsList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }));
          setContacts(contactsList);
        } catch (error) {
          console.error('Error fetching contacts:', error);
        }
      }
    };
    
    if (auth.currentUser) {
      fetchContacts();
    }
  }, []);

  // רישום לעדכונים של sessions
  useEffect(() => {
    if (socket && Object.keys(callsMade).length > 0) {
      Object.values(callsMade).forEach(callInfo => {
        if (callInfo.call_id) {
          socket.emit('register_session', {
            sessionId: callInfo.call_id,
            userId: auth.currentUser?.uid
          });
        }
      });
    }

    return () => {
      if (socket && Object.keys(callsMade).length > 0) {
        Object.values(callsMade).forEach(callInfo => {
          if (callInfo.call_id) {
            socket.emit('unregister_session', {
              sessionId: callInfo.call_id
            });
          }
        });
      }
    };
  }, [socket, callsMade]);

  // פונקציה להתחלת שיחות - מקבלת את המצב כפרמטר
  const startCallsWithMode = async (diggingMode = false, terrorMode = false) => {
    console.log('BILLING DEBUG: Starting calls with balance:', userBalance, 'terrorMode:', terrorMode, 'diggingMode:', diggingMode);

    if (!selectedContacts.length) {
      toast({
        title: 'שגיאה',
        description: 'אנא בחר אנשי קשר',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    // בדיקת הגבלת שיחות
    if (!userDocument?.features?.unlimited_calls && selectedContacts.length > 10) {
      toast({
        title: 'הגבלת שיחות',
        description: 'החשבון שלך מוגבל ל-10 שיחות בכל פעם',
        status: 'warning',
        duration: 5000,
        isClosable: true
      });
      return;
    }

    if (!diggingMode && !terrorMode && !agentPhone) {
      toast({
        title: 'שגיאה',
        description: 'אנא מלא את המספר שלך (נדרש למצב רגיל)',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (connectionStatus !== 'connected') {
      toast({
        title: 'שגיאה',
        description: 'אין חיבור לשרת - נסה שוב כעבור זמן קצר',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    // רענן יתרה לפני תחילת השיחה
    const currentBalance = await fetchUserBalance();
    console.log('BILLING DEBUG: Current balance before call:', currentBalance);

    // בדיקת יתרה רק למצב רגיל
    if (!diggingMode && !terrorMode) {
      const estimatedMaxCost = selectedContacts.length * 0.29;
      if (currentBalance < estimatedMaxCost) {
        toast({
          title: 'יתרה לא מספקת',
          description: `יתרה נוכחית: $${currentBalance.toFixed(2)}. נדרש: $${estimatedMaxCost.toFixed(2)} (הערכה למצב רגיל)`,
          status: 'warning',
          duration: 5000,
          isClosable: true
        });
        return;
      }
    }

    const mode = terrorMode ? 'טרור' : diggingMode ? 'חפירה' : 'רגיל';
    setCallStatus(`מתחיל שיחות במצב ${mode}...`);
    setIsCallActive(true);
    setRealTimeUpdates({});

    try {
      for (const contactId of selectedContacts) {
        const contact = contacts.find((c) => c.id === contactId);
        if (contact) {
          const requestBody = {
            to_number: formatPhoneNumber(contact.phone),
            max_attempts: maxAttempts,
            user_id: auth.currentUser.uid
          };

          if (terrorMode) {
            requestBody.mode = 'terror';
          } else if (diggingMode) {
            requestBody.mode = 'digging';
          } else {
            requestBody.agent_number = formatPhoneNumber(agentPhone);
          }

          const response = await fetch(`${serverUrl}/api/make_call`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

          if (response.ok) {
            const data = await response.json();
            
            setCallsMade((prev) => ({
              ...prev,
              [contact.id]: {
                number: contact.phone,
                status: 'initiated',
                call_id: data.session_id,
                contact_name: contact.name,
                attempts: 0,
                started_at: new Date().toISOString(),
                mode: data.mode || 'normal',
                digging_info: data.mode === 'digging' ? {
                  interval_seconds: data.interval_seconds,
                  auto_hangup_seconds: data.auto_hangup_seconds,
                  no_charges: data.no_charges
                } : null,
                terror_info: data.mode === 'terror' ? {
                  simultaneous_calls: data.simultaneous_calls,
                  interval_seconds: data.interval_seconds,
                  auto_hangup_seconds: data.auto_hangup_seconds,
                  no_charges: data.no_charges
                } : null
              }
            }));

            if (socket) {
              socket.emit('register_session', {
                sessionId: data.session_id,
                userId: auth.currentUser?.uid
              });
            }

            await saveCallToFirebase({
              sessionId: data.session_id,
              status: 'initiated',
              mode: data.mode || 'normal',
              to_number: formatPhoneNumber(contact.phone),
              agent_number: (diggingMode || terrorMode) ? null : formatPhoneNumber(agentPhone),
              started_at: new Date().toISOString(),
              attempt: 1
            }, contact);

          } else {
            const error = await response.json();
            console.error(`Failed to start call for ${contact.name}:`, error);
            toast({
              title: 'שגיאה',
              description: error.message || `נכשל בהתחלת שיחה ל${contact.name}`,
              status: 'error',
              duration: 3000,
              isClosable: true
            });
          }
        }
      }

      setCallStatus(`שיחות פעילות במצב ${mode}`);
      toast({
        title: `✅ שיחות החלו במצב ${mode}`,
        description: terrorMode
          ? `🔥 ${selectedContacts.length} מתקפות טרור החלו - ללא חיובים!`
          : diggingMode
          ? `${selectedContacts.length} שיחות חפירה החלו - ללא חיובים!`
          : `${selectedContacts.length} שיחות החלו. חיוב רק על שיחות שנענו!`,
        status: 'success',
        duration: 4000,
        isClosable: true
      });

    } catch (error) {
      console.error('שגיאה בהתחלת השיחות:', error);
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בהתחלת השיחות',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      setIsCallActive(false);
      setCallStatus('שגיאה');
    }
  };

  // פונקציה להפסקת שיחות
  const stopCalls = async () => {
    try {
      const stopPromises = Object.values(callsMade).map(async (callInfo) => {
        if (callInfo.call_id) {
          await saveCallToFirebase({
            sessionId: callInfo.call_id,
            status: 'cancelled',
            details: 'השיחה בוטלה על ידי המשתמש',
            timestamp: new Date().toISOString()
          });

          if (socket) {
            socket.emit('unregister_session', {
              sessionId: callInfo.call_id
            });
          }
          
          const response = await fetch(`${serverUrl}/api/end_call/${callInfo.call_id}`, {
            method: 'POST'
          });

          if (!response.ok) {
            console.error(`Failed to stop call ${callInfo.call_id}`);
          }
          
          return response;
        }
      });

      await Promise.all(stopPromises);

      setIsCallActive(false);
      setCallStatus('הופסק');
      setRealTimeUpdates({});
      
      toast({
        title: '🛑 השיחות הופסקו',
        description: 'כל השיחות הפעילות הופסקו',
        status: 'info',
        duration: 3000,
        isClosable: true
      });

    } catch (error) {
      console.error('שגיאה בהפסקת השיחות:', error);
      toast({
        title: 'שגיאה',
        description: 'נכשל בהפסקת השיחות',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  // תרגום סטטוסים מעודכן
  const getStatusText = (status, mode) => {
    if (mode === 'digging') {
      const diggingStatusTexts = {
        'digging_started': 'חפירה החלה',
        'digging_call': 'שיחת חפירה',
        'digging_ringing': 'מצלצל',
        'digging_answered': 'נענה',
        'digging_hangup': 'נותק',
        'digging_ended': 'הסתיים',
        'digging_completed': 'הושלם',
        'digging_error': 'שגיאה'
      };
      return diggingStatusTexts[status] || status;
    }
    
    const statusTexts = {
      'initiated': 'נוצר',
      'attempting': 'מנסה',
      'dialing': 'חייג',
      'ringing': 'מצלצל',
      'answered': 'נענה',
      'connecting_agent': 'מתחבר לסוכן',
      'agent_connected': 'מחובר לסוכן',
      'agent_answered': 'סוכן ענה',
      'charged': 'חויב',
      'no_charge': 'ללא חיוב',
      'busy': 'קו תפוס',
      'failed': 'נכשל',
      'error': 'שגיאה טכנית',
      'timeout': 'לא ענה',
      'rejected': 'נדחה',
      'retrying': 'מנסה שוב',
      'completed': 'הושלם',
      'ended': 'הסתיים',
      'cancelled': 'בוטל'
    };
    return statusTexts[status] || status;
  };

  // פונקציית הצגת סטטוס מעודכנת
  const getStatusDisplay = (callInfo) => {
    const sessionId = callInfo.call_id;
    const realtimeData = realTimeUpdates[sessionId];
    
    if (realtimeData) {
      const { status, details, attempt, timestamp, cost_info, rate, mode } = realtimeData;
      
      let color = 'gray';
      let icon = <Spinner size="sm" />;
      let showBilling = false;
      let billingIcon = null;
      
      if (mode === 'terror') {
        billingIcon = <Icon as={MdMoneyOff} color="green.500" />;

        switch (status) {
          case 'terror_started':
          case 'terror_wave':
            color = 'red';
            icon = <Icon as={MdCallSplit} />;
            break;
          case 'terror_call':
            color = 'orange';
            icon = <Icon as={MdCall} />;
            break;
          case 'terror_answered':
            color = 'yellow';
            icon = <Icon as={MdCallReceived} />;
            break;
          case 'terror_completed':
            color = 'gray';
            icon = <CheckIcon />;
            break;
          case 'terror_failed':
            color = 'red';
            icon = <WarningIcon />;
            break;
          default:
            color = 'red';
            icon = <Icon as={MdCallSplit} />;
        }
      } else if (mode === 'digging') {
        billingIcon = <Icon as={MdMoneyOff} color="green.500" />;

        switch (status) {
          case 'digging_started':
            color = 'purple';
            icon = <Icon as={MdLoop} />;
            break;
          case 'digging_call':
            color = 'blue';
            icon = <Icon as={MdCallSplit} />;
            break;
          case 'digging_ringing':
            color = 'yellow';
            icon = <Icon as={MdCallMade} />;
            break;
          case 'digging_answered':
            color = 'green';
            icon = <Icon as={MdCallReceived} />;
            break;
          case 'digging_hangup':
            color = 'orange';
            icon = <Icon as={MdCallEnd} />;
            break;
          case 'digging_completed':
            color = 'gray';
            icon = <CheckIcon />;
            break;
          case 'digging_error':
            color = 'red';
            icon = <WarningIcon />;
            break;
          default:
            color = 'purple';
            icon = <Icon as={MdLoop} />;
        }
      } else {
        switch (status) {
          case 'initiated':
            color = 'blue';
            icon = <Icon as={MdPhone} />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'attempting':
          case 'dialing':
            color = 'blue';
            icon = <Icon as={MdCall} />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'ringing':
            color = 'yellow';
            icon = <Icon as={MdCallMade} />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'answered':
            color = 'green';
            icon = <Icon as={MdCallReceived} />;
            billingIcon = <Icon as={MdAttachMoney} color="red.500" />;
            showBilling = true;
            break;
          case 'charged':
            color = 'orange';
            icon = <Icon as={MdAttachMoney} />;
            showBilling = true;
            break;
          case 'no_charge':
            color = 'green';
            icon = <Icon as={MdMoneyOff} />;
            break;
          case 'timeout':
            color = 'yellow';
            icon = <WarningIcon />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'busy':
            color = 'orange';
            icon = <NotAllowedIcon />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'rejected':
            color = 'red';
            icon = <SmallCloseIcon />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'error':
            color = 'red';
            icon = <WarningIcon />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'retrying':
            color = 'blue';
            icon = <Icon as={MdLoop} />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          case 'ended':
            color = 'gray';
            icon = <Icon as={MdCallEnd} />;
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
            break;
          default:
            billingIcon = <Icon as={MdMoneyOff} color="green.500" />;
        }
      }
      
      return (
        <VStack align="start" spacing={2}>
          <Flex align="center" gap={2} wrap="wrap">
            {icon}
            <Badge colorScheme={color} fontSize="sm" borderRadius="md">
              {getStatusText(status, mode)}
            </Badge>
            {billingIcon && (
              <Tooltip label={mode === 'digging' ? 'מצב חפירה - ללא חיוב' : (showBilling ? 'חיוב פעיל' : 'ללא חיוב')}>
                {billingIcon}
              </Tooltip>
            )}
            {attempt && (
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                {mode === 'digging' ? `שיחה #${attempt}` : `ניסיון ${attempt}`}
              </Text>
            )}
          </Flex>
          
          <Text fontSize="xs" color="gray.600" maxW="250px">
            {details}
          </Text>
          
          {mode === 'terror' && (
            <Box borderWidth={1} borderColor="red.200" borderRadius="md" p={2} bg="red.50">
              <Text fontSize="xs" fontWeight="bold" color="red.700">
                🔥 מצב טרור - ללא חיוב
              </Text>
            </Box>
          )}

          {mode === 'digging' && (
            <Box borderWidth={1} borderColor="purple.200" borderRadius="md" p={2} bg="purple.50">
              <Text fontSize="xs" fontWeight="bold" color="purple.700">
                🔄 מצב חפירה - ללא חיוב
              </Text>
            </Box>
          )}
          
          {mode !== 'digging' && showBilling && (
            <Box borderWidth={1} borderColor="orange.200" borderRadius="md" p={2} bg="orange.50">
              <Text fontSize="xs" fontWeight="bold" color="orange.700">
                💰 חיוב פעיל
              </Text>
              {rate && (
                <Text fontSize="xs" color="orange.600">
                  תעריף: {rate}
                </Text>
              )}
            </Box>
          )}
          
          {mode !== 'digging' && cost_info && (
            <Box borderWidth={1} borderColor="blue.200" borderRadius="md" p={2} bg="blue.50">
              <Text fontSize="xs" fontWeight="bold" color="blue.700">
                💸 חויב: ${cost_info.cost_usd?.toFixed(2)}
              </Text>
              {cost_info.billable_minutes && (
                <Text fontSize="xs" color="blue.600">
                  {cost_info.billable_minutes} דקות
                </Text>
              )}
            </Box>
          )}
          
          {timestamp && (
            <Text fontSize="xs" color="gray.400">
              {formatTimestamp(timestamp)}
            </Text>
          )}
        </VStack>
      );
    }
    
    return getStatusBadge(callInfo.status);
  };

  // פונקציות עזר נוספות
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('he-IL', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'initiated':
        return <Badge colorScheme="blue">מתחיל</Badge>;
      case 'in-progress':
        return <Badge colorScheme="green">פעיל</Badge>;
      case 'completed':
        return <Badge colorScheme="gray">הסתיים</Badge>;
      case 'failed':
        return <Badge colorScheme="red">נכשל</Badge>;
      case 'connected':
        return <Badge colorScheme="purple">מחובר</Badge>;
      case 'timeout':
        return <Badge colorScheme="yellow">לא ענה</Badge>;
      default:
        return <Badge colorScheme="yellow">לא ידוע</Badge>;
    }
  };

  // סטטיסטיקות
  const getCallStatistics = () => {
    const total = Object.keys(callsMade).length;
    const diggingCalls = Object.values(callsMade).filter(call => call.mode === 'digging').length;
    const normalCalls = total - diggingCalls;
    
    const completed = Object.values(realTimeUpdates).filter(u => 
      ['completed', 'charged', 'no_charge', 'digging_completed'].includes(u.status)
    ).length;
    const active = Object.values(realTimeUpdates).filter(u => 
      ['attempting', 'dialing', 'ringing', 'answered', 'connecting_agent', 'agent_connected', 'agent_answered', 'digging_call', 'digging_ringing'].includes(u.status)
    ).length;
    const failed = Object.values(realTimeUpdates).filter(u => 
      ['failed', 'error', 'timeout', 'busy', 'rejected', 'agent_failed', 'digging_error'].includes(u.status)
    ).length;
    const charged = Object.values(realTimeUpdates).filter(u => 
      u.status === 'charged' && u.cost_info?.cost_usd > 0
    ).length;
    const free = Object.values(realTimeUpdates).filter(u => 
      u.status === 'no_charge' || (u.status === 'completed' && (!u.cost_info || u.cost_info.cost_usd === 0)) ||
      u.mode === 'digging' || ['timeout', 'busy', 'rejected'].includes(u.status)
    ).length;

    return { total, completed, active, failed, charged, free, diggingCalls, normalCalls };
  };

  const stats = getCallStatistics();

  // אינדיקטור סטטוס חיבור
  const getConnectionIndicator = () => {
    let color, text, icon;
    
    switch (connectionStatus) {
      case 'connected':
        color = 'green';
        text = 'מחובר (Polling)';
        icon = <CheckIcon />;
        break;
      case 'disconnected':
        color = 'red';
        text = 'לא מחובר';
        icon = <NotAllowedIcon />;
        break;
      case 'error':
        color = 'yellow';
        text = 'מנסה להתחבר...';
        icon = <Spinner size="sm" />;
        break;
      default:
        color = 'yellow';
        text = 'מתחבר...';
        icon = <Spinner size="sm" />;
    }
    
    return (
      <Flex align="center" gap={2}>
        {icon}
        <Text fontSize="sm" color={`${color}.500`} fontWeight="medium">
          {text}
        </Text>
      </Flex>
    );
  };

  return (
    <Box mt={4}>
      <VStack spacing={6} align="stretch">
        {/* כרטיס יתרה ותמחור */}
        <Card variant="outline" borderColor={borderColor}>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">יתרה ותמחור</Heading>
              <Button size="sm" onClick={fetchUserBalance} variant="outline">
                רענן יתרה
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Stat>
                <StatLabel>יתרה נוכחית</StatLabel>
                <StatNumber color="green.500">${userBalance.toFixed(2)}</StatNumber>
                <StatHelpText>
                  ≈ ₪{(userBalance * 3.7).toFixed(2)}
                </StatHelpText>
              </Stat>
              
              {pricingInfo && (
                <Stat>
                  <StatLabel>תעריף שיחה רגילה</StatLabel>
                  <StatNumber color="blue.500">
                    ${pricingInfo.rates.per_minute_answered_usd}/דקה
                  </StatNumber>
                  <StatHelpText>רק על שיחות שנענו</StatHelpText>
                </Stat>
              )}
            </SimpleGrid>
            
            {pricingInfo && (
              <Alert status="info" mt={4} borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>מדיניות תמחור</AlertTitle>
                  <AlertDescription>
                    <VStack align="start" spacing={1}>
                      <Text>• <strong>מצב רגיל</strong>: חיוב רק על שיחות שנענו</Text>
                      <Text>• <strong>ניסיונות</strong>: ללא חיוב עד שהשיחה נענתה</Text>
                      <Text>• מינימום: דקה אחת (${pricingInfo.rates.minimum_charge_usd})</Text>
                      <Text>• <strong>מצב חפירה</strong>: ללא חיוב כלל! 🆓</Text>
                      <Text>• ללא דמי הקמה בכל המצבים</Text>
                    </VStack>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {pricingInfo?.digging_mode && (
              <Alert status="success" mt={4} borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>🔄 מצב חפירה זמין!</AlertTitle>
                  <AlertDescription>
                    <VStack align="start" spacing={1}>
                      <Text>• התקשרות חוזרת כל {pricingInfo.digging_mode.interval_seconds} שניות</Text>
                      <Text>• ניתוק אוטומטי אחרי {pricingInfo.digging_mode.auto_hangup_seconds} שניות</Text>
                      <Text>• ללא חיבור לסוכן - רק זיהוי מענה</Text>
                      <Text>• <strong>ללא חיוב כלל!</strong> 🎉</Text>
                    </VStack>
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* כרטיס הגדרות שיחה */}
        <Card variant="outline" borderColor={borderColor}>
          <CardHeader>
            <Heading size="md">הגדרות שיחה</Heading>
          </CardHeader>
          <CardBody>
            <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" colorScheme="teal">
              <TabList>
                <Tab>
                  <HStack>
                    <Icon as={PhoneIcon} />
                    <Text>מצב רגיל</Text>
                  </HStack>
                </Tab>
                {userDocument?.features?.digging_mode && (
                  <Tab>
                    <HStack>
                      <Icon as={MdLoop} />
                      <Text>מצב חפירה</Text>
                    </HStack>
                  </Tab>
                )}
                {userDocument?.features?.digging_mode && (
                  <Tab>
                    <HStack>
                      <Icon as={MdCallSplit} color="red.500" />
                      <Text color="red.500">🔥 מצב טרור</Text>
                    </HStack>
                  </Tab>
                )}
              </TabList>

              <TabPanels>
                {/* מצב רגיל */}
                <TabPanel>
                  <VStack spacing={4}>
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>מצב רגיל 💰</AlertTitle>
                        <AlertDescription>
                          שיחות עם חיבור לסוכן. חיוב רק על שיחות שנענו.
                        </AlertDescription>
                      </Box>
                    </Alert>

                    <Input
                      placeholder="המספר שלך (לחיבור השיחות)"
                      value={agentPhone}
                      onChange={(e) => setAgentPhone(e.target.value)}
                      size="lg"
                      focusBorderColor="teal.400"
                    />
                    
                    <Box w="100%">
                      <Text mb={2} fontSize="sm" color="gray.600">מספר ניסיונות מקסימלי</Text>
                      <NumberInput 
                        value={maxAttempts} 
                        onChange={(value) => setMaxAttempts(parseInt(value))}
                        min={1}
                        max={100}
                        size="lg"
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        כל הניסיונות ללא חיוב עד שהשיחה נענתה
                      </Text>
                    </Box>

                    {selectedContacts.length > 0 && pricingInfo && (
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>הערכת עלות מקסימלית</AlertTitle>
                          <AlertDescription>
                            אם כל השיחות ({selectedContacts.length}) יענו במצב רגיל: 
                            <Text fontWeight="bold" color="orange.600">
                              ${(selectedContacts.length * pricingInfo.rates.per_minute_answered_usd).toFixed(2)}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              (הנחה: דקה אחת לכל שיחה. חיוב רק על שיחות שנענו)
                            </Text>
                          </AlertDescription>
                        </Box>
                      </Alert>
                    )}
                  </VStack>
                </TabPanel>

                {/* מצב חפירה */}
                <TabPanel>
                  <VStack spacing={4}>
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>מצב חפירה 🆓</AlertTitle>
                        <AlertDescription>
                          שיחות חוזרות ללא סוכן. מטרה: לזהות מקבלי שיחות. ללא חיוב כלל!
                        </AlertDescription>
                      </Box>
                    </Alert>

                    <Box w="100%">
                      <Text mb={2} fontSize="sm" color="gray.600">מספר ניסיונות מקסימלי</Text>
                      <NumberInput 
                        value={maxAttempts} 
                        onChange={(value) => setMaxAttempts(parseInt(value))}
                        min={0}
                        max={999}
                        size="lg"
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        0 = ללא הגבלה (עצור ידנית)
                      </Text>
                    </Box>

                    <Box borderWidth={1} borderColor="green.200" borderRadius="md" p={4} bg="green.50" w="100%">
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="bold" color="green.700">ℹ️ איך עובד מצב חפירה?</Text>
                        <Text fontSize="sm" color="green.600">
                          • התקשרות חוזרת אוטומטית כל {pricingInfo?.digging_mode?.interval_seconds || 5} שניות
                        </Text>
                        <Text fontSize="sm" color="green.600">
                          • ניתוק אוטומטי אחרי {pricingInfo?.digging_mode?.auto_hangup_seconds || 8} שניות (אם נענה)
                        </Text>
                        <Text fontSize="sm" color="green.600">
                          • ללא חיבור לסוכן - רק זיהוי מענה
                        </Text>
                        <Text fontSize="sm" color="green.600">
                          • <strong>ללא חיוב כלל!</strong> 🎉
                        </Text>
                      </VStack>
                    </Box>

                    {selectedContacts.length > 0 && (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>שיחות חפירה</AlertTitle>
                          <AlertDescription>
                            {selectedContacts.length} אנשי קשר יקבלו שיחות חוזרות.
                            <Text fontWeight="bold" color="blue.600">
                              ללא עלות כלל! 🆓
                            </Text>
                          </AlertDescription>
                        </Box>
                      </Alert>
                    )}
                  </VStack>
                </TabPanel>

                {/* מצב טרור */}
                <TabPanel>
                  <VStack spacing={4}>
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>🔥 מצב טרור - הציקנות מקסימלית!</AlertTitle>
                        <AlertDescription>
                          5 שיחות במקביל כל שנייה. הטלפון לא יפסיק לצלצל! ללא חיוב.
                        </AlertDescription>
                      </Box>
                    </Alert>

                    <Box w="100%">
                      <Text mb={2} fontSize="sm" color="gray.600">מספר ניסיונות מקסימלי</Text>
                      <NumberInput
                        value={maxAttempts}
                        onChange={(value) => setMaxAttempts(parseInt(value))}
                        min={0}
                        max={999}
                        size="lg"
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        0 = ללא הגבלה (עצור ידנית)
                      </Text>
                    </Box>

                    <Box borderWidth={2} borderColor="red.300" borderRadius="md" p={4} bg="red.50" w="100%">
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="bold" color="red.700">🔥 איך עובד מצב טרור?</Text>
                        <Text fontSize="sm" color="red.600">
                          • 5 שיחות במקביל בכל גל!
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          • גל חדש כל שנייה אחת
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          • ניתוק אוטומטי אחרי 3 שניות
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          • רוטציה של מספרים מתקשרים
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          • ניתקו? מתקשר שוב מיד!
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          • <strong>ללא חיוב כלל!</strong> 🎉
                        </Text>
                      </VStack>
                    </Box>

                    {selectedContacts.length > 0 && (
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>🔥 מצב טרור</AlertTitle>
                          <AlertDescription>
                            {selectedContacts.length} אנשי קשר יקבלו מתקפת שיחות!
                            <Text fontWeight="bold" color="red.600">
                              הטלפון שלהם לא יפסיק לצלצל! ללא עלות 🆓
                            </Text>
                          </AlertDescription>
                        </Box>
                      </Alert>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>

            {/* כפתורי פעולה */}
            <VStack spacing={4} mt={6}>
              <HStack spacing={4} width="100%">
                <Button
                  leftIcon={activeTab === 0 ? <PhoneIcon /> : activeTab === 1 ? <Icon as={MdLoop} /> : <Icon as={MdCallSplit} />}
                  colorScheme={activeTab === 2 ? "red" : "teal"}
                  onClick={() => {
                    const newTerrorMode = activeTab === 2 && userDocument?.features?.digging_mode;
                    const newDiggingMode = activeTab === 1 && userDocument?.features?.digging_mode;
                    setIsTerrorMode(newTerrorMode);
                    setIsDiggingMode(newDiggingMode);
                    // קרא לפונקציה עם הערכים החדשים ישירות
                    startCallsWithMode(newDiggingMode, newTerrorMode);
                  }}
                  isDisabled={isCallActive || connectionStatus !== 'connected'}
                  size="lg"
                  flex={1}
                >
                  {activeTab === 0 ? 'התחל שיחות רגילות' : activeTab === 1 ? 'התחל מצב חפירה' : '🔥 התחל מצב טרור'}
                </Button>
                <Button
                  leftIcon={<SmallCloseIcon />}
                  colorScheme="red"
                  onClick={stopCalls}
                  isDisabled={!isCallActive}
                  size="lg"
                  flex={1}
                >
                  הפסק שיחות
                </Button>
              </HStack>

              <Flex justify="space-between" width="100%" align="center">
                <Text fontSize="sm" color="gray.600">סטטוס חיבור:</Text>
                {getConnectionIndicator()}
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* סטטיסטיקות */}
        {Object.keys(callsMade).length > 0 && (
          <Card variant="filled" bg={cardBg}>
            <CardHeader>
              <Heading size="md">סטטוס כללי</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4}>
                <Stat>
                  <StatLabel>סה"כ שיחות</StatLabel>
                  <StatNumber>{stats.total}</StatNumber>
                  <StatHelpText>
                    {stats.normalCalls > 0 && `${stats.normalCalls} רגילות`}
                    {stats.normalCalls > 0 && stats.diggingCalls > 0 && ' • '}
                    {stats.diggingCalls > 0 && `${stats.diggingCalls} חפירה`}
                  </StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>פעילות</StatLabel>
                  <StatNumber color="blue.500">{stats.active}</StatNumber>
                  <StatHelpText>שיחות פעילות</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>חויבו</StatLabel>
                  <StatNumber color="red.500">{stats.charged}</StatNumber>
                  <StatHelpText>שיחות שנענו</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>ללא חיוב</StatLabel>
                  <StatNumber color="green.500">{stats.free}</StatNumber>
                  <StatHelpText>לא נענו + חפירה</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>הושלמו</StatLabel>
                  <StatNumber color="purple.500">{stats.completed}</StatNumber>
                  <StatHelpText>סה"כ הושלמו</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>נכשלו</StatLabel>
                  <StatNumber color="red.500">{stats.failed}</StatNumber>
                  <StatHelpText>שיחות שנכשלו</StatHelpText>
                </Stat>
              </SimpleGrid>
              
              {stats.total > 0 && (
                <>
                  <Divider my={4} />
                  <VStack spacing={2}>
                    <Flex justify="space-between" width="100%">
                      <Text fontSize="sm">התקדמות השיחות</Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {Math.round(((stats.completed + stats.failed) / stats.total) * 100)}%
                      </Text>
                    </Flex>
                    <Progress 
                      value={((stats.completed + stats.failed) / stats.total) * 100} 
                      colorScheme="green" 
                      size="lg" 
                      width="100%"
                      borderRadius="md"
                    />
                    <HStack spacing={4} fontSize="xs" color="gray.600">
                      <Text>💰 {stats.charged} חויבו</Text>
                      <Text>✅ {stats.free} ללא חיוב</Text>
                      {stats.diggingCalls > 0 && <Text>🔄 {stats.diggingCalls} חפירה</Text>}
                    </HStack>
                  </VStack>
                </>
              )}
            </CardBody>
          </Card>
        )}

        {/* סטטוס נוכחי */}
        <Alert status={isCallActive ? 'info' : 'success'} borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>סטטוס מערכת</AlertTitle>
            <AlertDescription>
              {callStatus} {isCallActive && <Spinner size="sm" ml={2} />}
            </AlertDescription>
          </Box>
        </Alert>

        {/* טבלת שיחות */}
        {Object.keys(callsMade).length > 0 && (
          <Card variant="outline" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">מעקב שיחות בזמן אמת 📞🔄💰</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead>
                    <Tr>
                      <Th>שם</Th>
                      <Th>מספר טלפון</Th>
                      <Th>מצב</Th>
                      <Th>סטטוס מפורט</Th>
                      <Th>זמן התחלה</Th>
                      <Th>חיוב</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {Object.entries(callsMade).map(([contactId, callInfo]) => {
                      const contact = contacts.find((c) => c.id === contactId);
                      if (!contact) return null;

                      const sessionData = realTimeUpdates[callInfo.call_id];
                      const isTerror = callInfo.mode === 'terror' || sessionData?.mode === 'terror';
                      const isDigging = callInfo.mode === 'digging' || sessionData?.mode === 'digging';
                      const isChargeable = !isDigging && !isTerror && sessionData && 
                        ['answered', 'connecting_agent', 'agent_connected', 'agent_answered'].includes(sessionData.status);

                      return (
                        <Tr key={contactId} _hover={{ bg: hoverBg }}>
                          <Td fontWeight="medium">{contact.name}</Td>
                          <Td>
                            <Flex align="center" gap={2}>
                              <Icon as={PhoneIcon} />
                              {displayPhoneNumber(contact.phone)}
                            </Flex>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={isTerror ? 'red' : isDigging ? 'purple' : 'blue'}
                              fontSize="xs"
                            >
                              {isTerror ? '🔥 טרור' : isDigging ? '🔄 חפירה' : '📞 רגיל'}
                            </Badge>
                          </Td>
                          <Td>{getStatusDisplay(callInfo)}</Td>
                          <Td fontSize="sm" color="gray.600">
                            {callInfo.started_at ? 
                              new Date(callInfo.started_at).toLocaleTimeString('he-IL') : 
                              'N/A'
                            }
                          </Td>
                          <Td>
                            {isTerror ? (
                              <Badge colorScheme="red" fontSize="xs">
                                🔥 ללא חיוב
                              </Badge>
                            ) : isDigging ? (
                              <Badge colorScheme="green" fontSize="xs">
                                🆓 ללא חיוב
                              </Badge>
                            ) : isChargeable ? (
                              <Badge colorScheme="red" fontSize="xs">
                                💰 חיוב פעיל
                              </Badge>
                            ) : (
                              <Badge colorScheme="green" fontSize="xs">
                                ✅ ללא חיוב
                              </Badge>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* הודעה כאשר אין שיחות פעילות */}
        {Object.keys(callsMade).length === 0 && !isCallActive && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>מוכן להתחלה 🚀</AlertTitle>
              <AlertDescription>
                בחר אנשי קשר, בחר מצב שיחה והתחל.
                <br />
                <Text fontWeight="bold" color="blue.600">
                  • מצב רגיל: חיוב רק על שיחות שנענו 💰
                </Text>
                <Text fontWeight="bold" color="purple.600">
                  • מצב חפירה: ללא חיוב כלל! 🆓
                </Text>
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default CallSection;